import { Injectable } from '@angular/core';
import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { OneDriveGraphService } from './onedrive-graph.service';

@Injectable({
    providedIn: 'root'
})
export class CreateStreamService {
    private oneDriveUrl: string;

    public constructor(
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly oneDriveGraphService: OneDriveGraphService,
        private readonly instrumentationService: InstrumentationService) {
        }

    public createStreamContent(correlationId: string, fileType: FileType, useNewPlaylistCreationUrl?: boolean) {
        if (fileType === FileType.PlayLists || fileType === FileType.PlayList) {
            this.performAction(
                correlationId,
                FileType.PlayList,
                useNewPlaylistCreationUrl);
            this.instrumentationService.logClickEvent(
                KnownInstrumentationEvents.NewPlaylistButtonClick,
                'CreateNewPlaylist',
                KnownAreas.Templates,
                FileType.PlayList.toString());
        } else if (fileType === FileType.Stream) {
            this.performAction(
                correlationId,
                FileType.Stream);
            this.instrumentationService.logClickEvent(
                KnownInstrumentationEvents.NewVideoRecorderButtonClick,
                'CreateNewVideoRecorder',
                KnownAreas.Templates,
                FileType.Stream.toString());
        } else if (fileType === FileType.ScreenRecording) {
            this.performAction(
                correlationId,
                FileType.ScreenRecording);
            this.instrumentationService.logClickEvent(
                KnownInstrumentationEvents.NewScreenRecorderButtonClick,
                'CreateNewScreenRecorder',
                KnownAreas.Templates,
                FileType.Stream.toString());
        }
    }

    private async performAction(correlationId: string, fileType: FileType, useNewPlaylistCreationUrl?: boolean): Promise<void> {
        try {
            // Set OneDrive URL. If it fails or is not available, we will still try to fetch
            // it through UPA like before.
            const rootUrls = await this.oneDriveGraphService.getMyDriveRootUrl();
            const userRoot = rootUrls && rootUrls.userRoot;
            if (userRoot) {
                const documentsIndex = userRoot.lastIndexOf('/Documents');
                    if (documentsIndex === -1) {
                        this.instrumentationService.logErrorEvent(
                            KnownInstrumentationEvents.OneDriveUserRootUrl,
                            this.createInstrumentationParams(correlationId, fileType));
                        this.globalErrorHandler.handleError('userRootUrl not in expected format', ErrorId.sCtl9);
                    }

                this.oneDriveUrl = userRoot.substring(0, documentsIndex);
                let href = '';
                switch (fileType) {
                    case FileType.Stream:
                        href = `${this.oneDriveUrl}/_layouts/15/stream.aspx?action=create&enableFeatures=60543&cid=${correlationId}`;
                        break;
                    case FileType.ScreenRecording:
                        href = `${this.oneDriveUrl}/_layouts/15/stream.aspx?action=create&enableFeatures=60543&captureMode=screenrecorder&cid=${correlationId}`;
                        break;
                    default:
                        if (useNewPlaylistCreationUrl) {
                            href = `${this.oneDriveUrl}/_layouts/15/lists.aspx?openListTemplateId=3a867b4a-7429-0e1a-b02e-bf4b240ff1ce&workload=Stream&cid=${correlationId}`;
                        } else {
                            href = `${this.oneDriveUrl}/_layouts/15/lists.aspx?openCreate=true&workload=Stream&cid=${correlationId}`;
                        }
                        break;
                }
                window.open(href, '_blank');
            }
        } catch (err) {
            this.instrumentationService.logErrorEvent(
                fileType === FileType.PlayLists || fileType === FileType.PlayList ? KnownInstrumentationEvents.NewPlaylistButtonClick : fileType === FileType.ScreenRecording ? KnownInstrumentationEvents.NewScreenRecorderButtonClick : KnownInstrumentationEvents.NewVideoRecorderButtonClick,
                this.createInstrumentationParams(correlationId));
            return this.globalErrorHandler.handleError(err, ErrorId.sCtl9);
        }
    }

    private createInstrumentationParams(
        correlationId: string,
        fileType?: FileType
    ): InstrumentationParams {
        if (fileType) {
            return {
                [InstrumentationParam.ResourceId]: correlationId,
                [InstrumentationParam.DocumentType]: FileType[fileType]
            };
        }

        return {
            [InstrumentationParam.ResourceId]: correlationId
        };
    }
}
